/**
 * 页面 cover 内置图库（Notion pageCovers 的已采集子集）。
 *
 * 结构、分类顺序、每张图的默认 position 逐字取自 Notion bundle
 * `runtime-opfs-live-2026-07-27/archive/resources/acaae8d9….js`（chunk 5483，
 * `pageCovers` 模块）：分类含署名链接；item 带 `position`（缺省 0.5）。
 * Notion 共 12 个分类；采集档案里只有 Color & Gradient（11 张全）与
 * Texturelabs（12 张全）两类的图片实体，其余分类待补采后追加。
 */
import gradients2 from './cover-assets/gradients_2.png';
import gradients3 from './cover-assets/gradients_3.png';
import gradients4 from './cover-assets/gradients_4.png';
import gradients5 from './cover-assets/gradients_5.png';
import gradients8 from './cover-assets/gradients_8.png';
import gradients10 from './cover-assets/gradients_10.jpg';
import gradients11 from './cover-assets/gradients_11.jpg';
import solidBeige from './cover-assets/solid_beige.png';
import solidBlue from './cover-assets/solid_blue.png';
import solidRed from './cover-assets/solid_red.png';
import solidYellow from './cover-assets/solid_yellow.png';
import texturelabsConcrete146 from './cover-assets/texturelabs_concrete_146S.jpg';
import texturelabsFabric169 from './cover-assets/texturelabs_fabric_169S.jpg';
import texturelabsGlass124 from './cover-assets/texturelabs_glass_124S.jpg';
import texturelabsGlass125 from './cover-assets/texturelabs_glass_125S.jpg';
import texturelabsGlass132 from './cover-assets/texturelabs_glass_132S.jpg';
import texturelabsMetal126 from './cover-assets/texturelabs_metal_126S.jpg';
import texturelabsMetal212 from './cover-assets/texturelabs_metal_212S.jpg';
import texturelabsMetal261 from './cover-assets/texturelabs_metal_261S.jpg';
import texturelabsMetal285 from './cover-assets/texturelabs_metal_285S.jpg';
import texturelabsPaper253 from './cover-assets/texturelabs_paper_253S.jpg';
import texturelabsWater135 from './cover-assets/texturelabs_water_135S.jpg';
import texturelabsWood244 from './cover-assets/texturelabs_wood_244S.jpg';

export interface CoverGalleryItem {
  /** 持久化用的稳定 id（原始文件名） */
  id: string;
  /** 打包后的资源 URL */
  url: string;
  title: string;
  /** Notion 每张图的默认纵向位置，缺省 0.5 */
  position: number;
  /** 缩略图偏移（个别图与正文 position 不同） */
  thumbnailPosition?: number;
}

export interface CoverGalleryCategory {
  title: string;
  /** 分类标题的外部署名链接 */
  url: string;
  items: CoverGalleryItem[];
}

const item = (
  id: string,
  url: string,
  title: string,
  position = 0.5,
  thumbnailPosition?: number
): CoverGalleryItem => ({ id, url, title, position, thumbnailPosition });

export const coverGallery: CoverGalleryCategory[] = [
  {
    title: 'Color & Gradient',
    url: 'https://cargocollective.com/superfamousimages/36-Gradients',
    items: [
      item('solid_red.png', solidRed, 'Red', 0.6),
      item('solid_yellow.png', solidYellow, 'Yellow', 0.6),
      item('solid_blue.png', solidBlue, 'Blue', 0.6),
      item('solid_beige.png', solidBeige, 'Beige', 0.6),
      item('gradients_8.png', gradients8, 'Gradient 8', 0.3),
      item('gradients_4.png', gradients4, 'Gradient 4', 0.1),
      item('gradients_2.png', gradients2, 'Gradient 2', 0.8),
      item('gradients_11.jpg', gradients11, 'Gradient 11', 0.6),
      item('gradients_10.jpg', gradients10, 'Gradient 10', 0.6),
      item('gradients_5.png', gradients5, 'Gradient 5', 0.6),
      item('gradients_3.png', gradients3, 'Gradient 3', 0.4),
    ],
  },
  {
    title: 'Texturelabs',
    url: 'https://texturelabs.org',
    items: [
      item(
        'texturelabs_glass_132S.jpg',
        texturelabsGlass132,
        'Frosted Glass',
        0.6
      ),
      item(
        'texturelabs_metal_126S.jpg',
        texturelabsMetal126,
        'Army Green Painted Metal'
      ),
      item(
        'texturelabs_metal_261S.jpg',
        texturelabsMetal261,
        'Aqua Color Old Paint'
      ),
      item(
        'texturelabs_glass_125S.jpg',
        texturelabsGlass125,
        'Hazy Glass',
        0.7
      ),
      item(
        'texturelabs_fabric_169S.jpg',
        texturelabsFabric169,
        'Abstract Paint Canvas',
        1,
        0.8
      ),
      item(
        'texturelabs_glass_124S.jpg',
        texturelabsGlass124,
        'Hazy Frosted Glass'
      ),
      item(
        'texturelabs_metal_212S.jpg',
        texturelabsMetal212,
        'Oxidized Rusted Metal'
      ),
      item(
        'texturelabs_metal_285S.jpg',
        texturelabsMetal285,
        'Matte Aged Copper'
      ),
      item('texturelabs_water_135S.jpg', texturelabsWater135, 'Ice Surface'),
      item(
        'texturelabs_concrete_146S.jpg',
        texturelabsConcrete146,
        'Weathered Concrete',
        0.5,
        1
      ),
      item(
        'texturelabs_wood_244S.jpg',
        texturelabsWood244,
        'Black Painted Age Wood'
      ),
      item(
        'texturelabs_paper_253S.jpg',
        texturelabsPaper253,
        'Blue Vintage Paper'
      ),
    ],
  },
];

export const findGalleryItem = (id: string): CoverGalleryItem | undefined => {
  for (const category of coverGallery) {
    const hit = category.items.find(i => i.id === id);
    if (hit) return hit;
  }
  return undefined;
};

/**
 * Notion 的随机 cover：先均匀随机取分类，再随机取分类内一张
 * （bundle `890603` 模块函数 l 的算法），返回图与其默认 position。
 */
export const randomGalleryItem = (): CoverGalleryItem => {
  const category =
    coverGallery[Math.floor(Math.random() * coverGallery.length)];
  return category.items[Math.floor(Math.random() * category.items.length)];
};
